import jwtDecode from 'jwt-decode';
import getApiAccessToken from '@core/utils/getApiAccessToken';
import requestProductAccess from '@core/utils/requestProductAccess';
import { clearSplashOrganization } from '@core/utils/startLoader';
import getUserUid from './getUserUid';

const ORGANIZATIONID_KEY = 'uop'; // as in (U)ser (O)rganization (P)roduct
const PRODUCT_ACCESS_KEY = 'pat'; // as in (P)roduct (A)ccess (T)oken
const FIVE_MINUTES = 300000;

export type OrganizationInfo = {
  organizationId?: string;
  hasMoreOrganizations?: boolean;
};

function isValidToken(token: string) {
  const jwt = jwtDecode(token) as any;
  const maxExp = (Date.now() + FIVE_MINUTES) / 1000;

  return jwt.exp > maxExp;
}

function organizationKey(userUid?: string) {
  return `${userUid}_${ORGANIZATIONID_KEY}`;
}

function productAccessKey(userUid?: string) {
  return `${userUid}_${PRODUCT_ACCESS_KEY}`;
}

function getProductAccessToken(userUid?: string): { accessToken: string; tokenClaims: any } | null {
  const token = sessionStorage.getItem(productAccessKey(userUid));
  return JSON.parse(token!);
}

function setProductAccessToken(token: { accessToken: string; tokenClaims: any }, userUid?: string) {
  const item = JSON.stringify(token);
  sessionStorage.setItem(productAccessKey(userUid), item);
}

const getOrganization = (userUid?: string): OrganizationInfo | null => {
  const item = localStorage.getItem(organizationKey(userUid));
  return JSON.parse(item!);
};

const setOrganization = (orgInfo: OrganizationInfo, userUid?: string) =>
  localStorage.setItem(organizationKey(userUid), JSON.stringify(orgInfo));

async function requestNewToken(userUid?: string) {
  const apiToken = await getApiAccessToken();
  const { organizationId } = getOrganization(userUid) ?? {};
  const orgId = organizationId || sessionStorage.getItem('organizationId');
  if (orgId && apiToken) {
    const response = await requestProductAccess(orgId, apiToken);
    return response?.accessToken;
  }

  return '';
}

const removeOrganization = (userUid?: string) => localStorage.removeItem(organizationKey(userUid));

// The sessionStorage cache is only written once the exchange resolves, so
// concurrent callers all miss it and each POST their own token request. On a
// cold load there are ~6 of them (useOrganization, ThemeProvider, Redirect, and
// one per outgoing request from the axios interceptor). Share the in-flight
// promise instead.
//
// Keyed by user + organization rather than being a bare promise: a caller that
// joins after setOrganization() switched orgs must not be handed the previous
// organization's token.
let inFlight: { key: string; promise: Promise<string | undefined> } | null = null;

const acquireProductTokenSilent = async (): Promise<string | undefined> => {
  const userUid = getUserUid();

  const { accessToken: existingToken } = getProductAccessToken(userUid) ?? {};

  const organizationInfo = getOrganization(userUid);

  // A malformed/truncated cached token makes jwtDecode throw; treat that as a
  // cache miss and re-exchange rather than taking down every caller.
  let hasUsableCachedToken = false;
  if (existingToken) {
    try {
      const decodedExistingToken = jwtDecode(existingToken) as { orgId?: string };
      hasUsableCachedToken =
        isValidToken(existingToken) &&
        decodedExistingToken?.orgId === organizationInfo?.organizationId;
    } catch {
      hasUsableCachedToken = false;
    }
  }

  // Fast path: sessionStorage is synchronous, so there is nothing to dedupe.
  if (existingToken && hasUsableCachedToken) {
    return existingToken;
  }

  const key = `${userUid ?? ''}:${organizationInfo?.organizationId ?? ''}`;
  if (inFlight?.key === key) {
    return inFlight.promise;
  }

  const promise = (async () => {
    const accessToken = await requestNewToken(userUid);
    if (!accessToken) return undefined;

    const claims = jwtDecode(accessToken);
    setProductAccessToken({ accessToken, tokenClaims: claims }, userUid);
    return accessToken;
  })().finally(() => {
    if (inFlight?.key === key) inFlight = null;
  });

  inFlight = { key, promise };
  return promise;
};

const removeProductToken = (userUid?: string) => {
  localStorage.setItem('logout-event', 'started');
  localStorage.removeItem('logout-event');
  sessionStorage.removeItem(productAccessKey(userUid));
  // Logged out: the next person on this browser may belong to another org.
  clearSplashOrganization();
};

const getProductClaims = (userUid?: string) => {
  const accessToken = getProductAccessToken(userUid);
  return accessToken?.tokenClaims;
};

const clearUserSession = (userUid?: string) => {
  removeOrganization(userUid);
  removeProductToken(userUid);
};

const productAccessManager = {
  getOrganization,
  setOrganization,
  removeOrganization,
  acquireProductTokenSilent,
  removeProductToken,
  getProductClaims,
  clearUserSession,
};

export default productAccessManager;
